/**
 * InfluentIntelligencePanel.tsx
 * Live influent classification panel for the DAF Spec page.
 * Separated into its own module so Rollup cannot DCE it.
 */
import { useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Activity, TriangleAlert, Atom, ScanSearch, Cpu,
  AlertCircle, CheckCircle2, Info, Beaker,
} from 'lucide-react';
import { classifyInfluent, type ClassifyParams } from '@/lib/dafClassify';

export function InfluentIntelligencePanel({ params }: { params: ClassifyParams }) {
  const intel = useMemo(() => classifyInfluent(params), [params]);

  const confidenceColor =
    intel.confidence === 'high'   ? '#10b981' :
    intel.confidence === 'medium' ? '#f59e0b' : 'var(--rb-text-2)';

  const conductionBg =
    intel.conductivityAlarm.level === 'critical' ? 'bg-red-500/10 border-red-500/40' :
    intel.conductivityAlarm.level === 'high'     ? 'bg-amber-500/10 border-amber-500/40' :
    intel.conductivityAlarm.level === 'moderate' ? 'bg-sky-500/10 border-sky-500/30' :
    'bg-slate-800/50 border-slate-800/40';

  const conductionIcon =
    intel.conductivityAlarm.level === 'critical' ? <TriangleAlert className="w-3.5 h-3.5 text-red-400 shrink-0" /> :
    intel.conductivityAlarm.level === 'high'     ? <TriangleAlert className="w-3.5 h-3.5 text-amber-400 shrink-0" /> :
    intel.conductivityAlarm.level === 'moderate' ? <Activity className="w-3.5 h-3.5 text-sky-400 shrink-0" /> :
    <Activity className="w-3.5 h-3.5 text-slate-500 shrink-0" />;

  const conductionText =
    intel.conductivityAlarm.level === 'critical' ? 'text-red-300' :
    intel.conductivityAlarm.level === 'high'     ? 'text-amber-300' :
    intel.conductivityAlarm.level === 'moderate' ? 'text-sky-300' :
    'text-slate-400';

  function flagIcon(level: string) {
    if (level === 'error')   return <TriangleAlert className="w-3 h-3 text-red-400 shrink-0 mt-0.5" />;
    if (level === 'warning') return <AlertCircle    className="w-3 h-3 text-amber-400 shrink-0 mt-0.5" />;
    if (level === 'ok')      return <CheckCircle2   className="w-3 h-3 text-emerald-400 shrink-0 mt-0.5" />;
    return                          <Info           className="w-3 h-3 text-sky-400 shrink-0 mt-0.5" />;
  }

  function flagText(level: string) {
    return level === 'error'   ? 'text-red-300' :
           level === 'warning' ? 'text-amber-300' :
           level === 'ok'      ? 'text-emerald-300' : 'text-sky-300';
  }

  const topSources   = intel.sources.slice(0, 5);
  const visibleFlags = intel.flags.filter(f => f.level !== 'ok').slice(0, 4);
  const okFlags      = intel.flags.filter(f => f.level === 'ok').slice(0, 2);

  return (
    <div className="md:col-span-3 overflow-hidden rb-card">

      {/* ── Header ── */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-800/80 bg-[#0d1e38]">
        <ScanSearch className="w-4 h-4 text-violet-400" />
        <span className="text-xs font-bold text-slate-200 tracking-wide">Influent Intelligence</span>
        <span className="flex items-center gap-1 ml-1">
          <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" />
          <span className="text-[10px] text-violet-400 font-medium">LIVE</span>
        </span>
        {intel.dataQuality === 'invalid' && (
          <span className="ml-auto text-[10px] font-bold text-red-400 bg-red-500/10 border border-red-500/30 rounded px-1.5 py-0.5">DATA ERROR</span>
        )}
        {intel.dataQuality === 'suspect' && (
          <span className="ml-auto text-[10px] font-bold text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5">SUSPECT DATA</span>
        )}
        {intel.dataQuality === 'good' && (
          <span className="ml-auto text-[10px] font-medium text-emerald-500">Data OK</span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 sm:divide-x divide-slate-800/60">

        {/* ── Column 1: Source Classification ── */}
        <div className="p-3 space-y-2">
          <div className="flex items-center gap-1.5 mb-1">
            <Atom className="w-3 h-3 text-violet-400" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Source ID</span>
          </div>

          {/* Primary source badge */}
          <div
            className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border"
            style={{ background: `${intel.primarySource.color}12`, borderColor: `${intel.primarySource.color}35` }}
          >
            <div className="w-2 h-2 rounded-full shrink-0" style={{ background: intel.primarySource.color }} />
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold truncate" style={{ color: intel.primarySource.color }}>
                {intel.primarySource.label}
              </p>
              <p className="text-[10px] text-slate-500 leading-tight">
                {intel.primarySource.probability}% match ·{' '}
                <span style={{ color: confidenceColor }}>{intel.confidence}</span> confidence
              </p>
            </div>
          </div>

          {/* Probability bars */}
          <div className="space-y-1.5">
            {topSources.map((src) => (
              <div key={src.id} className="flex items-center gap-1.5">
                <div
                  className="w-[72px] shrink-0 text-[10px] text-slate-500 truncate leading-tight"
                  title={src.label}
                >
                  {src.label.split(' / ')[0]}
                </div>
                <div className="flex-1 h-1.5 rounded-full bg-slate-800 overflow-hidden">
                  <motion.div
                    className="h-full rounded-full"
                    style={{ background: src.color, width: `${src.probability}%` }}
                    initial={{ width: 0 }}
                    animate={{ width: `${src.probability}%` }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                  />
                </div>
                <span
                  className="w-7 text-right text-[10px] font-mono shrink-0"
                  style={{ color: `${src.color}cc` }}
                >
                  {src.probability}%
                </span>
              </div>
            ))}
          </div>

          {intel.confidence === 'low' && (
            <p className="text-[10px] text-slate-600 italic leading-tight pt-0.5">
              No dominant source — mixed or complex effluent. Jar test recommended.
            </p>
          )}
        </div>

        {/* ── Column 2: Physics / Chemistry Checks ── */}
        <div className="p-3 space-y-1.5">
          <div className="flex items-center gap-1.5 mb-1">
            <Beaker className="w-3 h-3 text-sky-400" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Chem / Physics</span>
          </div>

          {/* Key ratios */}
          {[
            {
              label: 'BOD:COD',
              value: (params.cod > 0 ? params.bod / params.cod : 0).toFixed(2),
              note:  params.cod > 0 && params.bod / params.cod >= 0.4 && params.bod / params.cod <= 0.65
                ? 'good biodegradability'
                : params.cod > 0 && params.bod / params.cod < 0.2
                ? 'refractory' : 'check data',
            },
            {
              label: 'NH₃/COD',
              value: (params.cod > 0 ? params.nh3n / params.cod : 0).toFixed(3),
              note:  params.cod > 0 && params.nh3n / params.cod > 0.03 ? 'proteinaceous' : 'low N loading',
            },
            {
              label: 'O&G/TSS',
              value: (params.tss > 0 ? params.og / params.tss : 0).toFixed(2),
              note:  params.tss > 0 && params.og / params.tss > 0.5 ? 'high FOG fraction' : 'normal',
            },
            {
              label: 'TP/BOD',
              value: (params.bod > 0 ? params.tp / params.bod : 0).toFixed(3),
              note:  params.bod > 0 && params.tp / params.bod > 0.012 ? 'phosphorus-rich' : 'typical',
            },
          ].map((row) => (
            <div key={row.label} className="flex items-center gap-1.5 py-0.5 border-b border-slate-800/40 last:border-0">
              <span className="text-[10px] text-slate-500 w-14 shrink-0 font-mono">{row.label}</span>
              <span className="text-[10px] font-bold text-white tabular-nums w-10 shrink-0">{row.value}</span>
              <span className="text-[10px] text-slate-600 truncate">{row.note}</span>
            </div>
          ))}

          <div className="pt-1 space-y-1">
            {okFlags.map((f, i) => (
              <div key={i} className="flex items-start gap-1">
                {flagIcon(f.level)}
                <span className={`text-[10px] leading-tight ${flagText(f.level)}`}>{f.message}</span>
              </div>
            ))}
            {visibleFlags.map((f, i) => (
              <div key={i} className="flex items-start gap-1">
                {flagIcon(f.level)}
                <span className={`text-[10px] leading-tight ${flagText(f.level)}`}>{f.message}</span>
              </div>
            ))}
            {intel.flags.length === 0 && (
              <p className="text-[10px] text-emerald-600 italic">No flags — parameters within normal ranges.</p>
            )}
          </div>
        </div>

        {/* ── Column 3: Conductivity Alarm + DAF Advice ── */}
        <div className="p-3 space-y-2.5">

          {/* Conductivity / salinity */}
          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <Activity className="w-3 h-3 text-sky-400" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Salinity / Conductivity</span>
            </div>
            <div className={`rounded-lg border p-2 ${conductionBg}`}>
              <div className="flex items-start gap-1.5">
                {conductionIcon}
                <div className="min-w-0">
                  <p className={`text-[10px] font-bold leading-tight ${conductionText}`}>
                    {params.conductivity.toLocaleString()} µS/cm
                  </p>
                  {intel.conductivityAlarm.triggered ? (
                    <p className={`text-[10px] leading-snug mt-0.5 ${conductionText} opacity-80`}>
                      {intel.conductivityAlarm.message}
                    </p>
                  ) : (
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      Est. TDS {intel.conductivityAlarm.estTDS_mgL.toLocaleString()} mg/L ·{' '}
                      {intel.conductivityAlarm.salinity_ppt.toFixed(1)} ppt — normal
                    </p>
                  )}
                </div>
              </div>
              {intel.conductivityAlarm.triggered && (
                <div className="flex gap-3 mt-1.5 pt-1.5 border-t border-white/5">
                  <div>
                    <p className="text-[10px] text-slate-600 uppercase tracking-wide">Est. TDS</p>
                    <p className={`text-[10px] font-bold tabular-nums ${conductionText}`}>
                      {intel.conductivityAlarm.estTDS_mgL.toLocaleString()} mg/L
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-600 uppercase tracking-wide">Salinity</p>
                    <p className={`text-[10px] font-bold tabular-nums ${conductionText}`}>
                      {intel.conductivityAlarm.salinity_ppt.toFixed(1)} ppt
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* DAF recommendation */}
          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <Cpu className="w-3 h-3 text-violet-400" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">DAF Advice</span>
            </div>
            <div className="rounded-lg bg-violet-500/8 border border-violet-500/20 p-2 space-y-1.5">
              {[
                { label: 'Coagulant',  value: intel.dafAdvice.coagulantType },
                { label: 'Dose',       value: intel.dafAdvice.coagulantDoseHint },
                { label: 'HLR target', value: intel.dafAdvice.hlrHint },
                { label: 'Recycle',    value: intel.dafAdvice.recycleHint },
              ].map(row => (
                <div key={row.label} className="flex items-start gap-1.5">
                  <span className="text-[10px] uppercase tracking-wide text-slate-600 w-14 shrink-0 pt-0.5">
                    {row.label}
                  </span>
                  <span className="text-[10px] text-violet-300 leading-tight font-medium">{row.value}</span>
                </div>
              ))}
              <p className="text-[10px] text-slate-500 leading-snug pt-0.5 border-t border-violet-500/15">
                {intel.dafAdvice.rationale}
              </p>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
